/**
 * Instagram-style tap-zone geometry for the within-post media carousel.
 *
 * An image slide splits in half — left = previous, right = next. A focused
 * video slide reserves a wide center band for play/pause, flanked by narrow
 * prev/next edges so a deliberate edge tap navigates and a casual center tap
 * pauses (keeps accidental slide changes rare).
 */

export type TapMode = "image" | "video";
export type TapAction = "prev" | "next" | "toggle";

/**
 * Fraction of the media width taken by each edge (prev/next) zone in video
 * mode. The remaining center (1 - 2 * edge) is the play/pause zone.
 */
export const VIDEO_EDGE_FRACTION = 0.3;

function clamp01(value: number): number {
  if (Number.isNaN(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

/**
 * Map a horizontal tap position to a carousel action.
 * @param fraction 0 at the left edge of the media, 1 at the right edge.
 */
export function resolveTapAction(fraction: number, mode: TapMode): TapAction {
  const x = clamp01(fraction);
  if (mode === "image") {
    return x < 0.5 ? "prev" : "next";
  }
  if (x < VIDEO_EDGE_FRACTION) return "prev";
  if (x >= 1 - VIDEO_EDGE_FRACTION) return "next";
  return "toggle";
}

/** Advance one slide, wrapping past the end. Guards an empty carousel. */
export function nextIndex(current: number, count: number): number {
  if (count <= 0) return 0;
  return (current + 1) % count;
}

/** Retreat one slide, wrapping past the start. Guards an empty carousel. */
export function prevIndex(current: number, count: number): number {
  if (count <= 0) return 0;
  return (current - 1 + count) % count;
}
